import { FloatingPortal } from '@floating-ui/react';
import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { CSSProperties } from 'styled-components';

import { isDefined } from '../../../utils/variables';
import { useButtonHotkey } from '../../hooks/use-button-hotkey';
import { useElementWidth } from '../../hooks/use-element-width';
import { useKeyHandler } from '../../hooks/use-key-handler';
import { Button } from '../button/button';
import { Icon } from '../icon/icon';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';

export type DropdownSuggestion = {
  name: string | { filterName: string; node: React.ReactNode };
  id: string;
};

// Helper function to get the filter name from a suggestion
function getFilterName<T extends DropdownSuggestion>(suggestion: T): string {
  return typeof suggestion.name === 'string' ? suggestion.name : suggestion.name.filterName;
}

// Helper function to get the display node from a suggestion
function getDisplayNode<T extends DropdownSuggestion>(suggestion: T): React.ReactNode {
  return typeof suggestion.name === 'string' ? suggestion.name : suggestion.name.node;
}

function filterSuggestions<T extends DropdownSuggestion>(
  name: string,
  suggestions: T[],
  excludedSuggestions?: T[],
  limit?: number,
  filterPredicate?: (inputLowerCase: string, suggestion: T) => boolean,
) {
  const filtered = suggestions.filter((suggestion) => {
    const matches = filterPredicate
      ? filterPredicate(name, suggestion)
      : getFilterName(suggestion).toLowerCase().includes(name);
    return matches && !excludedSuggestions?.some((x) => x.id === suggestion.id);
  });
  return limit !== undefined ? filtered.slice(0, limit) : filtered;
}

/** Returns a slice of the list centered on the selected item when possible, so the dropdown opens showing items near the selection. */
function getSuggestionsWindow<T extends DropdownSuggestion>(
  allFiltered: T[],
  selectedId: string | undefined,
  limit: number,
): T[] {
  if (allFiltered.length <= limit) {
    return allFiltered;
  }
  if (!selectedId) {
    return allFiltered.slice(0, limit);
  }
  const selectedIndex = allFiltered.findIndex((s) => s.id === selectedId);
  if (selectedIndex < 0) {
    return allFiltered.slice(0, limit);
  }
  const half = Math.floor(limit / 2);
  const start = Math.max(0, Math.min(selectedIndex - half, allFiltered.length - limit));
  return allFiltered.slice(start, start + limit);
}

interface Props<T extends DropdownSuggestion> {
  id?: string;
  suggestions: T[];
  onValidateInput?: (input: string) => boolean;
  onAddInput?: (input: string) => void;
  excludedSuggestions?: T[];
  value?: string;
  onSuggestionSelected: (suggestion: T) => void;
  clearOnSelection?: boolean;
  width?: React.CSSProperties['width'];
  placeholder?: string;
  disabled?: boolean;
  alternativeSuggestionsLimit?: number;
  renderSuggestion?: (suggestion: T) => React.ReactNode;
  filterPredicate?: (inputLowerCase: string, suggestion: T) => boolean;
  onClear?: () => void;
  /**
   * When true, portal the suggestions list to document.body so it is not clipped by
   * parent overflow (e.g. inside a dialog). The list will use a high z-index to appear above dialogs.
   */
  portalToBody?: boolean;
}

const DropdownSuggestion = <T extends DropdownSuggestion>({
  id,
  suggestions,
  excludedSuggestions,
  onSuggestionSelected,
  onValidateInput,
  onAddInput,
  disabled = false,
  clearOnSelection = false,
  width = '200px',
  value = undefined,
  placeholder = '',
  renderSuggestion,
  filterPredicate,
  alternativeSuggestionsLimit,
  onClear,
  portalToBody = false,
}: Props<T>) => {
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<T[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isInputValid, setIsInputValid] = useState(false);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const previousValueRef = useRef<string | undefined>(value);
  const selectedSuggestionRef = useRef<T | undefined>(undefined);
  const { t } = useTranslation();

  const shownSuggestionsLimit = alternativeSuggestionsLimit ?? 5;

  const { refs, strategy, update, floatingStyles } = useFloating({
    middleware: [
      offset(0),
      flip(),
      shift(),
      size({
        padding: 8,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.min(availableHeight, 350)}px`;
        },
      }),
    ],
    placement: 'bottom-start',
  });

  useEffect(() => {
    if (isFocused && refs.reference.current && refs.floating.current) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
  }, [isFocused, refs.reference, refs.floating, update, filterPredicate]);

  // When the list opens, scroll the selected item into view so it's visible
  useEffect(() => {
    if (!isFocused || filteredSuggestions.length === 0 || !value || !refs.floating.current) {
      return;
    }
    const listEl = refs.floating.current;
    const selectedEl = listEl.querySelector<HTMLElement>('[data-selected]');
    if (selectedEl && typeof selectedEl.scrollIntoView === 'function') {
      const timeoutId = requestAnimationFrame(() => {
        selectedEl.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      });
      return () => cancelAnimationFrame(timeoutId);
    }
  }, [isFocused, filteredSuggestions.length, value, refs.floating]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = event.target.value;
    setInputValue(inputVal);
    setIsFocused(true);
    const all = filterSuggestions(
      inputVal.toLowerCase(),
      suggestions,
      excludedSuggestions,
      undefined,
      filterPredicate,
    );
    setFilteredSuggestions(getSuggestionsWindow(all, value, shownSuggestionsLimit));
  };

  useEffect(() => {
    setIsInputValid(onValidateInput?.(inputValue) ?? true);
  }, [inputValue, onValidateInput]);

  const handleSuggestionClick = useCallback(
    (suggestion: T) => {
      selectedSuggestionRef.current = suggestion;
      onSuggestionSelected(suggestion);
      if (clearOnSelection) {
        setInputValue('');
      } else {
        // Only set input value if name is a string, not a ReactNode
        const nameIsString = typeof suggestion.name === 'string';
        setInputValue(nameIsString ? getFilterName(suggestion) : '');
      }
      setFilteredSuggestions([]);
      setIsFocused(false);
    },
    [clearOnSelection, onSuggestionSelected],
  );

  // Handles updating the textbox if the value is specified.
  useEffect(() => {
    const previousValue = previousValueRef.current;
    previousValueRef.current = value;

    if (isDefined(value)) {
      const suggestion = suggestions.find((x) => x.id === value);

      if (isDefined(suggestion)) {
        // Store the selected suggestion for placeholder
        selectedSuggestionRef.current = suggestion;
        // Only update input value when not focused. When focused the user is
        // actively searching, so we must not clear what they have typed.
        // The input is cleared once on open inside handleClick instead.
        if (!isFocused) {
          const nameIsString = typeof suggestion.name === 'string';
          setInputValue(nameIsString ? getFilterName(suggestion) : '');
        }

        // When focused, filter by what the user has already typed so that
        // a suggestions-array update from the parent does not reset the list.
        const all = filterSuggestions(
          isFocused ? inputValue.toLowerCase() : '',
          suggestions,
          excludedSuggestions,
          undefined,
          filterPredicate,
        );
        setFilteredSuggestions(getSuggestionsWindow(all, value, shownSuggestionsLimit));
      } else {
        // If there is no matching suggestion to the value then we just clear the field.
        setInputValue('');
        setFilteredSuggestions([]);
        setIsFocused(false);
      }
    } else if (isDefined(previousValue) && !isDefined(value)) {
      // Only clear if value changed from defined to undefined (not if it was already undefined)
      // And don't clear if the field is focused (user is typing)
      selectedSuggestionRef.current = undefined;
      if (!isFocused) {
        setInputValue('');
        setFilteredSuggestions([]);
      }
    } else if (!isDefined(value)) {
      // Clear selected suggestion when value is undefined
      selectedSuggestionRef.current = undefined;
    }
    // Note: We don't handle the case where value is undefined and field is focused here
    // because handleClick and handleInputChange already handle showing suggestions
  }, [
    isFocused,
    inputValue,
    value,
    suggestions,
    excludedSuggestions,
    filterPredicate,
    shownSuggestionsLimit,
  ]);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if the click is on the clear button or inside it
      const isClickOnClearButton = target.tagName === 'BUTTON' || target.closest('button') !== null;

      const isClickInsideReference =
        isDefined(refs.reference.current) &&
        (refs.reference.current as HTMLElement).contains(event.target as Node);

      // Don't open suggestions if clicking the clear button
      if (isClickOnClearButton && isClickInsideReference) {
        return;
      }

      if (isClickInsideReference) {
        if (isFocused) {
          // Toggle: if already focused, close the suggestions
          setIsFocused(false);
          setFilteredSuggestions([]);
        } else {
          // Open suggestions — clear the input so the user can search from scratch
          setIsFocused(true);
          setInputValue('');
          const all = filterSuggestions(
            '',
            suggestions,
            excludedSuggestions,
            undefined,
            filterPredicate,
          );
          setFilteredSuggestions(getSuggestionsWindow(all, value, shownSuggestionsLimit));
        }
        return;
      }

      // Close if clicking outside both reference and floating elements
      if (
        isDefined(refs.floating.current) &&
        !refs.floating.current.contains(event.target as Node) &&
        !isClickInsideReference
      ) {
        setIsFocused(false);
        setFilteredSuggestions([]);
      }
    },
    [
      isFocused,
      suggestions,
      excludedSuggestions,
      value,
      refs.reference,
      refs.floating,
      filterPredicate,
      shownSuggestionsLimit,
    ],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [handleClick]);

  const handleOnInputAdded = () => {
    if (!isDefined(onAddInput) || !isInputValid) {
      return;
    }

    onAddInput(inputValue);
    setInputValue('');
    setIsFocused(false);
    setFilteredSuggestions([]);
  };

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setInputValue('');
      // Don't show suggestions when clearing - keep it closed
      setIsFocused(false);
      setFilteredSuggestions([]);
      onClear?.();
    },
    [onClear],
  );

  const shouldShowClearButton = isDefined(onClear) && (isDefined(value) || inputValue.length > 0);

  // Determine if we should show the ReactNode overlay
  const selectedSuggestionName =
    !isFocused &&
    isDefined(value) &&
    selectedSuggestionRef.current &&
    typeof selectedSuggestionRef.current.name !== 'string'
      ? getDisplayNode(selectedSuggestionRef.current)
      : null;

  // Determine the placeholder: use selected suggestion name when focused and value exists, otherwise use provided placeholder.
  // Suppress placeholder entirely when the ReactNode overlay is visible (it covers the input).
  const displayPlaceholder = selectedSuggestionName
    ? ''
    : isFocused &&
        isDefined(value) &&
        selectedSuggestionRef.current &&
        typeof selectedSuggestionRef.current.name === 'string'
      ? getFilterName(selectedSuggestionRef.current)
      : placeholder;

  const handleEscapeKey = useKeyHandler(
    'Escape',
    () => {
      setIsFocused(false);
      setFilteredSuggestions([]);
    },
    { enabled: isFocused, stopPropagation: true },
  );

  useButtonHotkey(confirmButtonRef, 'Enter');
  const { ref: componentRef, width: componentWidth } = useElementWidth();
  const allFilteredSuggestions = useMemo(
    () =>
      filterSuggestions(
        inputValue.toLowerCase(),
        suggestions,
        excludedSuggestions,
        undefined,
        filterPredicate,
      ),
    [inputValue, suggestions, excludedSuggestions, filterPredicate],
  );

  const hiddenSuggestionsCount = allFilteredSuggestions.length - filteredSuggestions.length;

  return (
    <Component ref={componentRef} width={width}>
      <StackContainer direction="row" spacing={0.5} width={width}>
        <InputWrapper $hasClearButton={shouldShowClearButton} ref={refs.setReference}>
          <TextBox
            disabled={disabled}
            id={id}
            placeholder={displayPlaceholder}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleEscapeKey}
          />
          {selectedSuggestionName && (
            <SelectedValueOverlay>{selectedSuggestionName}</SelectedValueOverlay>
          )}
          {shouldShowClearButton ? (
            <ClearButton aria-label="Clear" disabled={disabled} onClick={handleClear}>
              <Icon icon={['fas', 'times']} />
            </ClearButton>
          ) : (
            <CaretIcon disabled={disabled}>
              <Icon icon={['fas', 'chevron-down']} />
            </CaretIcon>
          )}
        </InputWrapper>
        {isDefined(onAddInput) && (
          <Button
            color="secondary"
            disabled={!isInputValid}
            leftIcon={{ icon: ['fad', 'plus'] }}
            noWrap={true}
            ref={confirmButtonRef}
            variant="contained"
            width="fit-content"
            onClick={handleOnInputAdded}
          >
            {t('add', { ns: 'common' })}
          </Button>
        )}
      </StackContainer>
      {isFocused && filteredSuggestions.length > 0 && (
        <FloatingPortal root={portalToBody ? undefined : componentRef.current}>
          <SuggestionsList
            className="notranslate"
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              position: strategy,
              zIndex: portalToBody ? 10001 : undefined,
            }}
            width={componentWidth}
          >
            {filteredSuggestions.map((suggestion, index) => (
              <SuggestionItem
                data-selected={suggestion.id === value ? true : undefined}
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <SuggestionContent>
                  {renderSuggestion ? renderSuggestion(suggestion) : getDisplayNode(suggestion)}
                </SuggestionContent>
              </SuggestionItem>
            ))}
            {hiddenSuggestionsCount > 0 && (
              <SuggestionItem
                key={'-1'}
                onClick={() => {
                  if (refs.reference.current && refs.floating.current) {
                    setFilteredSuggestions(allFilteredSuggestions);
                    autoUpdate(refs.reference.current, refs.floating.current, update);
                  }
                }}
              >
                <HiddenOptionsSpan>
                  {t('dropdownSuggestion.showHiddenOptions', {
                    ns: 'components',
                    count: hiddenSuggestionsCount,
                  })}
                </HiddenOptionsSpan>
              </SuggestionItem>
            )}
          </SuggestionsList>
        </FloatingPortal>
      )}
    </Component>
  );
};

const StackContainer = styled(Stack)<{ width: CSSProperties['width'] }>`
  width: ${({ width }) => width};
`;

const Component = styled.div<{ width: CSSProperties['width'] }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${({ width }) => width};
`;

const InputWrapper = styled.div<{ $hasClearButton?: boolean }>`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;

  /* Add padding to TextBox to prevent text overlap with icon */
  input {
    padding-right: 32px;
  }
`;

const SelectedValueOverlay = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 32px;
  bottom: 0;
  display: flex;
  align-items: center;
  padding-left: 0.5em;
  padding-right: 0.5em;
  pointer-events: none;
  z-index: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClearButton = styled.button<{ disabled?: boolean }>`
  position: absolute;
  right: 2px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 24px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition:
    background-color 0.2s,
    opacity 0.2s;
  z-index: 10;

  &:hover {
    background: ${(props) =>
      props.disabled ? props.theme.color.gray.lightest : props.theme.color.gray.light};
  }

  background: white;
  border: none;
  svg {
    font-size: 10px;
  }
`;

const CaretIcon = styled.div<{ disabled?: boolean }>`
  position: absolute;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
  width: 16px;
  height: 24px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  background: white;
  border: none;
  svg {
    font-size: 10px;
  }
`;

const SuggestionsList = styled.ul<{ width: number }>`
  margin: 0;
  padding: 0;
  list-style: none;
  background: white;
  border: 1px solid #888;
  overflow: hidden;
  overflow-y: auto;
  z-index: 1000;
  width: ${({ width }) => width}px; // Set the width directly on the list
  box-sizing: border-box; // Ensure borders/padding don’t affect the width
`;

const SuggestionItem = styled.li`
  padding: 0.5em;
  width: 100%;
  overflow: hidden;
  cursor: pointer;
  position: relative;

  &[data-selected] {
    background: ${({ theme }) => theme.color.selected};
    border-left: 3px solid ${({ theme }) => theme.color.selected};
    padding-left: calc(0.5em - 3px);
  }

  &:hover {
    background: ${({ theme }) => theme.color.brand.light};
  }
`;

const SuggestionContent = styled.div`
  width: 100%;
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
`;

const HiddenOptionsSpan = styled.span`
  font-style: italic;
  color: ${({ theme }) => theme.color.gray.medium};
`;

export { DropdownSuggestion };
